import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-accent/20 border-t border-white/5 mt-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div data-testid="footer-company-info">
            <div className="flex items-center space-x-2 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_hvac-renewables/artifacts/ixlsz35t_001%20GS%20logo.png" 
                alt="GreenSmart Logo" 
                className="h-10 w-auto"
              />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              Smart Building Solutions for a Sustainable Future. Zero capital investment with guaranteed energy savings.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/profile.php?id=61587358295832" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-muted-foreground hover:text-primary transition-colors">
                <Facebook size={20} />
              </a>
              <a href="https://twitter.com/GreenSmartLtd" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="text-muted-foreground hover:text-primary transition-colors">
                <Twitter size={20} />
              </a>
              <a href="https://www.linkedin.com/company/greensmart-ltd" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-muted-foreground hover:text-primary transition-colors">
                <Linkedin size={20} />
              </a>
              <a href="https://www.instagram.com/?hl=en" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-muted-foreground hover:text-primary transition-colors">
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div data-testid="footer-quick-links">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li><Link to="/products" onClick={() => window.scrollTo(0, 0)} className="text-sm text-muted-foreground hover:text-primary transition-colors">Products</Link></li>
              <li><Link to="/solutions" onClick={() => window.scrollTo(0, 0)} className="text-sm text-muted-foreground hover:text-primary transition-colors">Solutions</Link></li>
              <li><Link to="/about" onClick={() => window.scrollTo(0, 0)} className="text-sm text-muted-foreground hover:text-primary transition-colors">About Us</Link></li>
              <li><Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="text-sm text-muted-foreground hover:text-primary transition-colors">Blog</Link></li>
              <li><Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="text-sm text-muted-foreground hover:text-primary transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div data-testid="footer-resources">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-6">Resources</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href="https://www.bchydro.com/powersmart/business/programs.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  BC Hydro Rebates
                </a>
              </li>
              <li>
                <a 
                  href="https://www.fortisbc.com/rebates-and-energy-savings" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Fortis BC Rebates
                </a>
              </li>
              <li>
                <a 
                  href="https://www.egbc.ca/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Engineers & Geoscientists BC
                </a>
              </li>
              <li><Link to="/portal?type=agent" onClick={() => window.scrollTo(0, 0)} className="text-sm text-muted-foreground hover:text-primary transition-colors">Agent Portal</Link></li>
              <li><Link to="/portal?type=investor" onClick={() => window.scrollTo(0, 0)} className="text-sm text-muted-foreground hover:text-primary transition-colors">Investor Portal</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div data-testid="footer-contact">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone size={18} className="text-primary mt-1 flex-shrink-0" />
                <a href="tel:+17787292202" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  (778) 729-2202
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Mail size={18} className="text-primary mt-1 flex-shrink-0" />
                <a href="mailto:info@greensmart.ltd" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  info@greensmart.ltd
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-primary mt-1 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">
                  Vancouver, BC, Canada<br />
                  Toronto, ON, Canada<br />
                  Miami, FLA, US
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-xs text-muted-foreground">
              © {currentYear} GreenSmart Ltd. A GreenSmart Division — Vancouver, BC, Canada. All rights reserved.
            </p>
            <p className="text-xs text-muted-foreground/60 mt-1">
              Confidential and proprietary. Not for redistribution without written consent.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-xs text-muted-foreground hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-xs text-muted-foreground hover:text-primary transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
